/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { PureComponent, Fragment } from "react";
import { isFunction, isFinite, debounce } from "utils/lodashUtils";
import PagesViewCss from "./PagesView.css";
import Grid from "react-virtualized/dist/commonjs/Grid";
import { accessibilityOverscanIndicesGetter } from "react-virtualized/dist/commonjs";
import Wait from "@react/react-spectrum/Wait";
import { AJS_CUSTOM_SCROLL_EVT } from "constants/constants";
import { docViewModes, pagesViewLoader } from "constants/enums";
import { ScrubberContainer } from "containers/scrubber/ScrubberContainer";
import addEventListener from "dc-preview-utils/src/Utils/DomUtils";
import { updatePageAreaRects, triggerDocumentViewStateEvent } from "publicApis/Events/events";
import * as stores from "stores";
import { isValidPageIndex } from "selectors/pageSelectors";
import classNames from "classnames";
import { SWITCH_PREVIEW_STATE } from "constants/enums";
import { triggerDocumentViewUpdateEvent } from "publicApis/Events/events";

const emptyObj = {};
const noOp = () => {};
class PagesView extends PureComponent {
  constructor(props) {
    super(props);
    this._setElementHandle = this._setElementHandle.bind(this);
    this._noContentRenderer = this._noContentRenderer.bind(this);
    this._reactVirtualizedGrid = null;
  }

  _setGridRef(handle) {
    this._reactVirtualizedGrid = handle;
    this.props.setElementHandle(handle);
    updatePageAreaRects(stores);
  }

  createPage({ key, rowIndex, columnIndex, style }) {
    const PageContainer = this.props.PageContainer;
    const currPageIndex = this.props.currPageIndex;
    const pageIndex = this.props.getPageIndex(rowIndex, columnIndex, this.props.columnCount);
    if (!isValidPageIndex(stores, pageIndex)) {
      return <Fragment />;
    }
    return (
      <PageContainer
        key={key || pageIndex}
        pageIndex={pageIndex}
        style={style || emptyObj}
        currPageIndex={currPageIndex}
        loadNonPDFPageCanvas={this.props.loadNonPDFPageCanvas}
      />
    );
  }

  _setElementHandle(handle) {
    if (handle) {
      this.elementHandle = handle;
      this.props.setNonReactVPagesViewHandle(handle);
      !this.props.useReactVirtualized && this.props.setElementHandle(handle);
    }
  }

  _focus() {
    if (this.elementHandle) this.elementHandle.focus();
  }

  componentDidMount() {
    // Trigger triggerDocumentViewStateEvent if pageX, pageY, rotation, zoomLevel, and pageNumber are altered
    triggerDocumentViewUpdateEvent(stores);
    triggerDocumentViewStateEvent(stores);
    if (this.elementHandle) {
      if (this.props.useReactVirtualized === false) {
        this._setScrollTop();
        this.props.onPagesInViewChange({
          start: this.props.pagesInViewStart,
          end: this.props.pagesInViewEnd
        });
        this._removeScrollHandler = addEventListener(this.elementHandle, "scroll", this.props.onScroll);
        if (this.props.onScrollEnd) {
          this._removeScrollDebouncedHandler = addEventListener(
            this.elementHandle,
            "scroll",
            debounce(this.props.onScrollEnd, 300)
          );
        }
      } else {
        if (this._reactVirtualizedGrid) {
          const rowOffset = this._reactVirtualizedGrid.getOffsetForCell({
            alignment: "start",
            rowIndex: this.props.currPageIndex
          });
          this._reactVirtualizedGrid.scrollToPosition({ scrollTop: rowOffset.scrollTop });
        }
        this._removeCustomScrollHandler = addEventListener(window, AJS_CUSTOM_SCROLL_EVT, this.props.customScroll);
        this._removeCustomScrollDebouncedHandler = addEventListener(
          window,
          AJS_CUSTOM_SCROLL_EVT,
          debounce(this.props.customScrollEnd, 300)
        );
      }
      if (this.props.setFocusMethod) {
        this.props.setFocusMethod(this._focus.bind(this));
      }
      if (this.props.onPagesViewMount) {
        this.props.onPagesViewMount();
      }
    }
  }

  componentWillUnmount() {
    if (isFunction(this.props.onUnmount)) {
      this.props.onUnmount();
    }
    this.disposer && this.disposer();
    this._removeScrollHandler && this._removeScrollHandler();
    this._removeScrollDebouncedHandler && this._removeScrollDebouncedHandler();
    this._removeCustomScrollHandler && this._removeCustomScrollHandler();
    this._removeCustomScrollDebouncedHandler && this._removeCustomScrollDebouncedHandler();
  }

  _setScrollTop() {
    if (
      isFinite(this.props.pagesViewYPos) &&
      !this.props.isScrolling &&
      this.elementHandle.scrollTop !== this.props.pagesViewYPos
    ) {
      this.elementHandle.scrollTop = this.props.pagesViewYPos;
    }
  }

  _setScrollLeft() {
    if (
      isFinite(this.props.pagesViewXPos) &&
      !this.props.isScrolling &&
      this.elementHandle.scrollLeft !== this.props.pagesViewXPos
    ) {
      this.elementHandle.scrollLeft = this.props.pagesViewXPos;
    }
  }

  componentDidUpdate(prevProps, prevState) {
    triggerDocumentViewUpdateEvent(stores);
    triggerDocumentViewStateEvent(stores);
    if (this.props.useReactVirtualized === false) {
      if (prevProps.useReactVirtualized === true) {
        this._removeScrollHandler && this._removeScrollHandler();
        this._removeScrollHandler = addEventListener(this.elementHandle, "scroll", this.props.onScroll);
      }
      this._setScrollLeft();
      this._setScrollTop();
      if (
        prevProps.pagesInViewStart !== this.props.pagesInViewStart ||
        prevProps.pagesInViewEnd !== this.props.pagesInViewEnd
      ) {
        this.props.onPagesInViewChange({
          start: this.props.pagesInViewStart,
          end: this.props.pagesInViewEnd
        });
      }
    }
  }

  _recomputePageSizes() {
    if (this._reactVirtualizedGrid && this.props.shouldRecomputePageSizes === true) {
      for (let index = 0; index < this.props.pageCount; index++) {
        this._reactVirtualizedGrid.recomputeGridSize({
          rowIndex: index,
          columnIndex: 0
        });
      }
      this.props.resetShouldRecomputePageSizes();
    }
  }

  _noContentRenderer() {
    const ShowNoContentRenderer = this.props.showNoContentRenderer;
    if (ShowNoContentRenderer && this.props.isModernViewerEnabled) {
      return <ShowNoContentRenderer />;
    }
    return <Wait size="S" centered />;
  }

  render() {
    const { margins, viewMode, bottomPanelHeight = 0, documentBottomSpace = 0 } = this.props;
    let { parentViewHeight, shouldUseMobileModernViewer, hideStickyToolOnTap } = this.props;
    if (shouldUseMobileModernViewer && !hideStickyToolOnTap) {
      parentViewHeight -= bottomPanelHeight + documentBottomSpace;
    }

    const touchSelectStyle = {
      "-webkit-touch-callout": "none"
    };
    const { isRHPResizing } = this.props;
    const style = this.props.useReactVirtualized
      ? {
          ...(margins &&
            margins.left === "auto" &&
            margins.right === "auto" && { marginLeft: "auto", marginRight: "auto" }),
          ...(shouldUseMobileModernViewer ? touchSelectStyle : {})
        }
      : {
          paddingTop: `${this.props.topPadding}px`,
          overflowX: isRHPResizing ? "hidden" : "auto",
          overflowY: viewMode === docViewModes.IN_PLACE ? "auto" : "scroll",
          height: this.props.parentViewHeight
        };
    if (this.props.bgColor) {
      style.backgroundColor = this.props.bgColor;
    }

    let gridStyle = {
      overflowY: "auto",
      outline: "none"
    };

    let pageArray = [];
    if (this.props.useReactVirtualized === true) {
      this._recomputePageSizes();
    } else {
      for (let index = 0; index < this.props.pageCount; index++) {
        pageArray.push(this.createPage({ rowIndex: index }));
      }
    }
    let scrollInfo = {
      scrollLeft: this.props.pagesViewXPos
    };
    if (this._reactVirtualizedGrid && this.elementHandle) {
      const pagesViewHeight = this.elementHandle.clientHeight;
      const grid = this.elementHandle.querySelector(".ReactVirtualized__Grid");
      if (grid) {
        const gridScrolltop = grid.scrollTop;
        const gridScrollHeight = grid.scrollHeight;

        if (this.props.pagesViewYPos < gridScrolltop || gridScrollHeight - gridScrolltop > pagesViewHeight) {
          scrollInfo = Object.assign(scrollInfo, {
            scrollTop: this.props.pagesViewYPos
          });
        }
      }
      const innerGrid = this.elementHandle.querySelector(".ReactVirtualized__Grid__innerScrollContainer");
      if (innerGrid) {
        if (
          this.props.pagesViewLoader === pagesViewLoader.CLASSIC_VIEW_CONTAINER &&
          parentViewHeight >= innerGrid.clientHeight
        ) {
          gridStyle.display = "flex";
          gridStyle.alignItems = "center";
        }
        const pageOverlays = this.props.pageOverlays;
        if (pageOverlays && pageOverlays.length) {
          if (pageOverlays[pageOverlays.length - 1].clientName === "edit") {
            innerGrid.style.overflow = "visible";
          } else {
            innerGrid.style.overflow = "hidden";
          }
        }
      }
    }

    let PagesViewCssClass = classNames(PagesViewCss.PagesView, {
      [PagesViewCss.HighlightPagesView]: this.props.shouldHighlightGridOnFocus,
      [PagesViewCss.PagesViewResize]: isRHPResizing,
      [PagesViewCss.animated]: this.props.switchPreviewState !== SWITCH_PREVIEW_STATE.NONE,
      [PagesViewCss.slideOut]: this.props.switchPreviewState === SWITCH_PREVIEW_STATE.PRE_SWITCH,
      [PagesViewCss.slideIn]: this.props.switchPreviewState === SWITCH_PREVIEW_STATE.SWITCHED
    });

    return (
      <div
        id="pages-view"
        className={PagesViewCssClass}
        style={style}
        ref={this._setElementHandle}
        onKeyDown={this.props.onKeyDown}
        onMouseDown={this.props.onMouseDown}
        onClick={this.props.onClick}
        data-testid={this.props["data-testid"] ? this.props["data-testid"] : null}
      >
        {this.props.shouldShowScrubber ? <ScrubberContainer /> : null}
        {this.props.useReactVirtualized ? (
          <Grid
            style={gridStyle}
            role={this.props.role || "main"}
            cellRenderer={this.createPage.bind(this)}
            columnCount={this.props.columnCount}
            containerStyle={style}
            containerProps={{ ...(this.props.onFocusHandler && { onFocus: this.props.onFocusHandler }) }}
            className={this.props.className}
            columnWidth={this.props.getMaxWidth}
            estimatedRowSize={this.props.estimatedRowSize}
            estimatedColumnSize={this.props.estimatedColumnSize}
            height={parentViewHeight}
            noContentRenderer={this._noContentRenderer}
            overscanRowCount={this.props.overscanRowCount}
            overscanColumnCount={1}
            rowCount={this.props.totalRows}
            rowHeight={this.props.getPageViewHeight}
            onScroll={this.props.onScroll}
            onSectionRendered={defaultArgs =>
              this.props.onSectionRendered({ ...defaultArgs, colCount: this.props.columnCount })
            }
            ref={this._setGridRef.bind(this)}
            {...scrollInfo}
            width={this.props.containerWidth || this.props.parentViewWidth}
            aria-label={this.props.intl.formatMessage({ id: "accessibility.containerLabel" })}
            containerRole="presentation"
            // Grid applies aria-readonly which is invalid attribute for main/presentation, hence removing
            // eslint-disable-next-line jsx-a11y/aria-proptypes
            aria-readonly={null}
            overscanIndicesGetter={accessibilityOverscanIndicesGetter}
          />
        ) : (
          pageArray
        )}
      </div>
    );
  }
}

PagesView.defaultProps = {
  onClick: noOp,
  setElementHandle: noOp,
  customScrollEnd: noOp,
  className: "",
  margins: {
    left: "auto",
    right: "auto"
  }
};

export { PagesView };
